import { API_URL } from '@/config/constants'
export interface Room {
  name: string
  type: string
  capacity: number
}

export interface ClassSchedule {
  class: string
  [day: string]: any // allow any property, but we'll type days below
}

// Import the global TimetableData type
import { TimetableData } from '@/types/timetable'
import i18n from '@/utils/i18n'

export const fetchRooms = async (): Promise<Room[]> => {
  try {
    const response = await fetch(`${API_URL}/classroom`, {
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })

    if (!response.ok) throw new Error('Failed to fetch rooms')

    const data = await response.json()
    return data.map((room: any) => ({
      name: room.name,
      type: room.type,
      capacity: room.capacity,
    }))
  } catch (error) {
    // console.error('Error fetching rooms:', error)
    throw error
  }
}

export const getOccupiedRooms = (
  timetableData: TimetableData,
  day: string,
  time: string,
  currentClass: string
): string[] => {
  const occupiedRooms = new Set<string>()

  if (!timetableData?.scheduleData) return []

  for (const classSchedule of timetableData.scheduleData) {
    // Skip current class
    if (classSchedule.class === currentClass) continue

    const daySchedule = classSchedule[day]
    if (!daySchedule || !Array.isArray(daySchedule)) continue

    // Check all lessons at this day
    for (const lesson of daySchedule) {
      if (lesson.time === time && lesson.salle) {
        occupiedRooms.add(lesson.salle)
      }
    }
  }

  return Array.from(occupiedRooms)
}

export const checkTeacherAvailability = (
  timetableData: TimetableData,
  teacherId: string,
  day: string,
  time: string,
  currentClass: string
): boolean => {
  if (!timetableData?.scheduleData) return true

  for (const classSchedule of timetableData.scheduleData) {
    if (classSchedule.class === currentClass) continue

    const daySchedule = classSchedule[day]
    if (!daySchedule || !Array.isArray(daySchedule)) continue

    for (const lesson of daySchedule) {
      const lessonTeacherId = String(
        (lesson as any).teacherId ?? (lesson as any).teacherID ?? ''
      ).trim()
      if (lesson.time === time && lessonTeacherId === teacherId.trim()) {
        return false // Teacher is already teaching at this time
      }
    }
  }

  return true
}

export const checkRoomAvailability = (
  timetableData: TimetableData,
  roomName: string,
  day: string,
  time: string,
  currentClass: string
): boolean => {
  if (!timetableData?.scheduleData) return true

  for (const classSchedule of timetableData.scheduleData) {
    if (classSchedule.class === currentClass) continue

    const daySchedule = classSchedule[day]
    if (!daySchedule || !Array.isArray(daySchedule)) continue

    for (const lesson of daySchedule) {
      if (lesson.time === time && lesson.salle === roomName) {
        return false // Room is already occupied at this time
      }
    }
  }

  return true
}

export interface ScheduleConflict {
  type: 'teacher' | 'room' | 'invalid_data'
  message: string
  details?: {
    conflictingClass?: string
    subject?: string
    roomName?: string
    timeSlot?: string
  }
}

export const checkScheduleConflicts = (
  timetableData: TimetableData,
  day: string,
  time: string,
  teacherId: string | undefined,
  roomName: string | undefined,
  currentClass: string
): ScheduleConflict | null => {
  // Input validation
  if (!timetableData?.scheduleData) {
    return {
      type: 'invalid_data' as const,
      message: i18n.t('timetable:logic.room.errors.timetableDataNotAvailable'),
    }
  }

  // Teacher ID is optional for room-only checks. If provided, perform teacher conflict checks; otherwise, skip them.

  // Normalize inputs
  const normalizedDay = day.trim()
  const normalizedTime = time.trim()
  const normalizedTeacherId = teacherId?.trim() || ''
  const normalizedRoomName = roomName?.trim() // Make this optional
  const normalizedCurrentClass = currentClass.trim()

  // First check current class for conflicts at the new time
  const currentClassSchedule = timetableData.scheduleData.find(
    (s) => s.class?.trim() === normalizedCurrentClass
  )

  if (currentClassSchedule) {
    const daySchedule = currentClassSchedule[normalizedDay]
    if (Array.isArray(daySchedule)) {
      const existingLesson = daySchedule.find(
        (lesson) =>
          lesson.time?.trim() === normalizedTime &&
          (normalizedTeacherId &&
            String((lesson as any).teacherId ?? (lesson as any).teacherID ?? '')
              .trim() === normalizedTeacherId)
      )

      // If we're changing only the room (roomName provided), don't block due to the same lesson in the same class/time
      if (existingLesson && !normalizedRoomName) {
        return {
          type: 'teacher',
          message: i18n.t('timetable:logic.room.errors.teacherAlreadyTeaching', {
            class: normalizedCurrentClass,
          }),
          details: {
            conflictingClass: normalizedCurrentClass,
            subject: existingLesson.subject,
            timeSlot: normalizedTime,
          },
        }
      }
    }
  }

  // Check other classes for conflicts
  for (const classSchedule of timetableData.scheduleData) {
    const classScheduleClass = classSchedule.class?.trim()
    if (classScheduleClass === normalizedCurrentClass) continue

    const daySchedule = classSchedule[normalizedDay]
    if (!daySchedule || !Array.isArray(daySchedule)) continue

    for (const lesson of daySchedule) {
      if (lesson.time?.trim() === normalizedTime) {
        const subjectValue = lesson.subject?.trim() || i18n.t('timetable:logic.room.labels.aSubject')
        const classValue = classScheduleClass || i18n.t('timetable:logic.room.labels.anotherClass')
        // Check teacher conflict
        if (normalizedTeacherId) {
          const lessonTeacherId = String(
            (lesson as any).teacherId ?? (lesson as any).teacherID ?? ''
          ).trim()
          if (lessonTeacherId === normalizedTeacherId) {
            return {
              type: 'teacher',
              message: i18n.t('timetable:logic.room.errors.teacherAlreadyTeachingOther', {
                subject: subjectValue,
                class: classValue,
              }),
              details: {
                conflictingClass: classScheduleClass,
                subject: lesson.subject,
                timeSlot: normalizedTime,
              },
            }
          }
        }

        // Only check room conflict if roomName was provided
        if (normalizedRoomName) {
          // Use the same occupied-rooms logic as the dialog for consistency
          const occupied = getOccupiedRooms(
            timetableData,
            normalizedDay,
            normalizedTime,
            normalizedCurrentClass
          )
          if (occupied.includes(normalizedRoomName)) {
            return {
              type: 'room',
              message: i18n.t('timetable:logic.room.errors.roomAlreadyInUse', {
                roomName: normalizedRoomName,
                class: classValue,
                subject: subjectValue,
              }),
              details: {
                conflictingClass: classScheduleClass,
                subject: lesson.subject,
                roomName: normalizedRoomName,
                timeSlot: normalizedTime,
              },
            }
          }
        }
      }
    }
  }

  return null // No conflicts found
}
